import { toast } from '../modals.js';
import { esc } from '../utils.js';
import './password-meter.js';

/**
 * <wdc-change-password> — change your OWN password: the current one, a new one
 * with the strength meter, and the new one again. Shared by both shells, so
 * the two forms cannot drift, the way <wdc-mail-settings> is:
 *
 *   self-service  Password page     (account/js/components/password.js)
 *   admin         My Account page   (admin/js/components/me.js)
 *
 *   el.configure({
 *     save: (body) => Promise<{surfaces}>,  // PUT /me/password {current_password, new_password}
 *     context: [login, first, last],         // words it must not contain
 *     intro: 'plain text',                    // what this password is for
 *     title: 'Change password',               // the card's; '' for none (a page already titled "Password")
 *   });
 *
 * Both call the same route: /me/password takes the uid from the session and
 * handles a directory account and the local break-glass one alike.
 *
 * On success it says what the change reached. One password here can reach
 * mail, SMB shares and Wi-Fi, and someone whose phone stops collecting mail
 * an hour later should have been told, not left to work it out.
 */
class WdcChangePassword extends HTMLElement {
  #save = null;
  #busy = false;

  configure({ save, context = [], intro = '', title = 'Change password' }) {
    this.#save = save;
    this.innerHTML = `
      <div class="card">
        ${title ? `<div class="card-header"><span>${esc(title)}</span></div>` : ''}
        <div class="card-body">
          ${intro ? `<p class="text-muted">${esc(intro)}</p>` : ''}
          <form novalidate autocomplete="off">
            <label class="form-field">
              <span>Current password</span>
              <input name="current_password" type="password" autocomplete="current-password" required>
            </label>

            <label class="form-field">
              <span>New password</span>
              <input name="new_password" type="password" autocomplete="new-password" required>
            </label>
            <wdc-password-meter for="new_password"></wdc-password-meter>

            <label class="form-field">
              <span>New password again</span>
              <input name="confirm" type="password" autocomplete="new-password" required>
            </label>

            <div class="alert alert-danger" data-pw="error" hidden></div>
            <div class="alert alert-success" data-pw="done" hidden></div>

            <button class="btn btn-primary" type="submit" data-pw="submit">Change password</button>
          </form>
        </div>
      </div>`;

    this.querySelector('wdc-password-meter').context = context;
    this.querySelector('form').addEventListener('submit', e => this.#submit(e));
  }

  async #submit(e) {
    e.preventDefault();
    if (this.#busy) return;

    const f = e.target.elements;
    const error = this.querySelector('[data-pw="error"]');
    const done = this.querySelector('[data-pw="done"]');
    const button = this.querySelector('[data-pw="submit"]');

    error.hidden = true;
    done.hidden = true;

    this.#busy = true;
    button.disabled = true;
    button.textContent = 'Changing…';

    try {
      // Caught here so a typo does not cost a round trip — the API checks all
      // of this again regardless.
      const refusal = f.new_password.value !== f.confirm.value ? 'Those two passwords do not match'
        : f.new_password.value === f.current_password.value ? 'New password must be different from the current one'
        : await this.querySelector('wdc-password-meter').check();
      if (refusal) return this.#fail(refusal);

      const res = await this.#save({
        current_password: f.current_password.value,
        new_password: f.new_password.value,
      });

      // Clear immediately: a password has no business sitting in the DOM, and
      // it is never put in state or localStorage at any point.
      f.current_password.value = f.new_password.value = f.confirm.value = '';

      const where = Array.isArray(res?.surfaces) && res.surfaces.length
        ? `<br>It now applies to: ${res.surfaces.map(esc).join(', ')}.`
        : '';
      done.innerHTML = `Your password has been changed.${where}`;
      done.hidden = false;
      toast('Password changed', 'success');
    } catch (err) {
      this.#fail(err.message ?? 'Could not change your password');
    } finally {
      this.#busy = false;
      button.disabled = false;
      button.textContent = 'Change password';
    }
  }

  #fail(message) {
    const error = this.querySelector('[data-pw="error"]');
    error.textContent = message;
    error.hidden = false;
  }
}

customElements.define('wdc-change-password', WdcChangePassword);
